import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Response, Pagination, ChangeLog } from '@ui/src';
import { CASP } from '@ui/src/components/transactions-sidepanels/types';
import { accountsListApi } from '../../accounts/store/services';
import {
  ExternalAccount,
  ExternalAccountTopUpOption,
  LinkedClient,
  PspIntegration,
  PspPartner,
} from '../types';

import ROUTES from '../../../routes';

export interface ExternalAccounstListState {
  pagination?: Pagination;
  externalAccounts?: ExternalAccount[];
  externalAccount?: ExternalAccount;
  pspPartners?: PspPartner[];
  takeCount?: number;
  casp?: CASP[];
  pspIntegrations?: PspIntegration[];
  openPayNotificationVisible?: boolean;
  externalAccountTopUpOptions?: ExternalAccountTopUpOption[];
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
  linkedClients?: LinkedClient[];
  linkedClientsPagination?: Pagination;
  linkedClientsTakeCount?: number;
}

const initialState: ExternalAccounstListState = {};

export const externalAccountsListSlice = createSlice({
  name: 'externalAccountsList',
  initialState,
  reducers: {
    setTakeCount: (state, action: PayloadAction<number>) => {
      state.takeCount = action.payload;
    },
    setLinkedClientTakeCount: (state, action: PayloadAction<number>) => {
      state.linkedClientsTakeCount = action.payload;
    },
    clearExternalAccountState: (state) => {
      state.externalAccount = undefined;
      state.externalAccountTopUpOptions = undefined;
      state.changeLogs = undefined;
      state.changeLogsPagination = undefined;
    },
    clearAccountsState: (state) => {
      state.externalAccounts = undefined;
      state.pagination = undefined;
    },
    clearLinkedClientsState: (state) => {
      state.linkedClients = undefined;
      state.linkedClientsPagination = undefined;
    },
    setOpenPayNotificationVisible: (state, action: PayloadAction<boolean>) => {
      state.openPayNotificationVisible = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      accountsListApi.endpoints.getExternalAccountsList.matchFulfilled,
      (state, action: PayloadAction<Response<any>>) => {
        state.externalAccounts = action?.payload?.data?.edges?.map((item) => ({
          pspPartnerId: '-',
          currencyId: '-',
          paymentType: '-',
          accountId: '-',
          accountName: '-',
          accountType: '-',
          active: false,
          url: generatePath(ROUTES.EXTERNAL_ACCOUNT, {
            accountId: item.node.id,
          }),
          ...item.node,
        }));
        state.pagination = action?.payload?.data?.pageInfo;
      },
    );
    builder.addMatcher(
      accountsListApi.endpoints.getExternalAccountChangeLogs.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<ChangeLog>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.changeLogs = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextChangeLogs = ([] as ChangeLog[]).concat(
            state.changeLogs as ChangeLog[],
          );

          action.payload.data.edges?.forEach((item) => {
            nextChangeLogs.push(item.node);
          });

          state.changeLogs = nextChangeLogs;
        }

        state.changeLogsPagination = action.payload.data?.pageInfo;
      },
    );
    builder.addMatcher(
      accountsListApi.endpoints.getPspIntegrations.matchFulfilled,
      (state, action: PayloadAction<Response<PspIntegration>>) => {
        state.pspIntegrations = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      accountsListApi.endpoints.getAccountsLinkedClients.matchFulfilled,
      (state, action: PayloadAction<Response<LinkedClient>>) => {
        state.linkedClients = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );
        state.linkedClientsPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      accountsListApi.endpoints.getExternalAccountTopUpOptions.matchFulfilled,
      (state, action: PayloadAction<Response<ExternalAccountTopUpOption>>) => {
        state.externalAccountTopUpOptions = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      accountsListApi.endpoints.deletePaymentAccounts.matchFulfilled,
      (state, action: PayloadAction<{ data: ExternalAccountTopUpOption }>) => {
        state.externalAccountTopUpOptions =
          state?.externalAccountTopUpOptions?.filter(
            (item) => item.id !== action.payload?.data?.id,
          );
      },
    );
    builder.addMatcher(
      accountsListApi.endpoints.updateExternalAccountCurrencies.matchFulfilled,
      (state, action: PayloadAction<{ data: ExternalAccount }>) => {
        state.externalAccounts = state.externalAccounts?.map((item) => {
          const result = { ...item };

          if (result.id === action.payload.data.id) {
            result.currenciesIds = action.payload.data.currenciesIds;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      accountsListApi.endpoints.getAccountsPspPartners.matchFulfilled,
      (state, action: PayloadAction<Response<PspPartner>>) => {
        state.pspPartners = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      accountsListApi.endpoints.getExternalAccount.matchFulfilled,
      (state, action: PayloadAction<{ data: ExternalAccount }>) => {
        state.externalAccount = action.payload.data;
      },
    );
    builder.addMatcher(
      accountsListApi.endpoints.getAccountsCASP.matchFulfilled,
      (state, action: PayloadAction<Response<CASP>>) => {
        state.casp = action?.payload?.data?.edges?.map((item) => item.node);
      },
    );
    builder.addMatcher(
      accountsListApi.endpoints.updateExternalAccountStatus.matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                active: boolean;
                ids: string[];
              };
            };
          };
        },
      ) => {
        const nextExternalAccounts = state.externalAccounts?.map((item) => {
          const result = { ...item };

          if (result.id === action?.meta.arg?.originalArgs?.ids[0]) {
            result.active = action?.meta.arg?.originalArgs.active;
          }

          return result;
        });

        state.externalAccounts = nextExternalAccounts;
      },
    );
  },
});

export const {
  setTakeCount,
  clearExternalAccountState,
  setOpenPayNotificationVisible,
  setLinkedClientTakeCount,
  clearAccountsState,
  clearLinkedClientsState,
} = externalAccountsListSlice.actions;

export default externalAccountsListSlice.reducer;
